import { defineStore } from "pinia";
import { Permission, Role } from "./PermissionsTypes";
export const AdminRole: Role = "admin";
export const ContributorRole: Role = "contributor";
export const ViewerRole: Role = "viewer";

const usePermissions = defineStore("PermissionsProvider", () => {
  // All permissions types
  const deletePermission: Permission = "project.delete";
  const editPermission: Permission = "project.edit";
  const viewPermission: Permission = "project.view";
  const managePermission: Permission = "project.manageusers";

  // Permissions based on roles
  const roleBasedPermissions: Record<Role, Permission[]> = {
    [AdminRole]: [deletePermission, editPermission, viewPermission, managePermission],
    [ContributorRole]: [editPermission, viewPermission, managePermission],
    [ViewerRole]: [viewPermission],
  };

  const getPermissionsFromRole = (role: Role) => roleBasedPermissions[role];

  const getRoleFromPermissions = (permissions: Permission[]) => {
    // NB: All roles are a perfect subset of admin, so these .every checks are confusing
    if (
      permissions.every((x) => roleBasedPermissions.admin.includes(x)) &&
      roleBasedPermissions.admin.every((x) => permissions.includes(x))
    ) {
      return AdminRole;
    }

    if (
      permissions.every((x) => roleBasedPermissions.contributor.includes(x)) &&
      roleBasedPermissions.contributor.every((x) => permissions.includes(x))
    ) {
      return ContributorRole;
    }

    if (
      permissions.every((x) => roleBasedPermissions.viewer.includes(x)) &&
      roleBasedPermissions.viewer.every((x) => permissions.includes(x))
    ) {
      return ViewerRole;
    }

    return ViewerRole;
  };

  // Note: Below is only to keep compability with backend stuff not in hasura
  // but found it easier to keep it here than in all the components using this.
  // Read: this can be deleted later when we have refactored
  const mapRole = (role: Role) => {
    return role.replace("project.", "") as Role;
  };

  const canDelete = (roles: Role[]) => roles.map(mapRole).includes(AdminRole);

  // const canEdit = (roles: Role[]) => roles.includes(ContributorRole) || roles.includes(AdminRole);

  const canEdit = (roles: Role[]) => roles.map(mapRole).includes(ContributorRole) || roles.map(mapRole).includes(AdminRole);

  const canView = (roles: Role[]) =>
    roles.map(mapRole).includes(ViewerRole) ||
    roles.map(mapRole).includes(ContributorRole) ||
    roles.map(mapRole).includes(AdminRole);
  const canManage = (roles: Role[]) => roles.map(mapRole).includes(ContributorRole) || roles.map(mapRole).includes(AdminRole);

  const isAdmin = (permissions: Permission[]) => getRoleFromPermissions(permissions) === AdminRole;
  const isAdminRole = (role: Role) => mapRole(role) === AdminRole;

  const isContributer = (permissions: Permission[]) => getRoleFromPermissions(permissions) === ContributorRole;
  const isViewer = (permissions: Permission[]) => getRoleFromPermissions(permissions) === ViewerRole;

  return {
    getPermissionsFromRole,
    getRoleFromPermissions,
    canDelete,
    canEdit,
    canView,
    canManage,
    isAdmin,
    isAdminRole,
    isContributer,
    isViewer,
  };
});

export default usePermissions;
